/* eslint-disable react/button-has-type */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex } from 'rebass';
import { Typography, UserCard, Slider, Slide } from '@oca/ui';
import { Trans } from '@lingui/macro';
import { propOr, getFullName } from '@lib/help-fns';
import { SkeletonRenderer } from '@lib/skeleton-loader';
import { isRTL } from '@lib/i18n';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Col, Row } from '@lib/grid';
import { Card } from '@features/dashboard/atoms';
import {
  CardBody,
  CardHeader,
  CardTitle,
} from '@features/dashboard/atoms/card';
import {
  ArrowLeft,
  ArrowRight,
  VideoCamera2,
  TimeCircle,
  CalendarEdit,
  Attach,
  Tasks,
  PersonalCard,
} from '@oca/icons';
import CircleButton from '@oca/ui/atoms/circle-button';

const propOrNa = propOr(<Trans>N/A</Trans>);
const formatFull = date => dayjs(date).format('L LT');
const format = date => dayjs(date).format('L');

const sliderOptions = {
  gap: '1rem',
  perPage: 6,
  breakpoints: {
    768: { perPage: 3 },
    1024: { perPage: 4 },
  },
  direction: isRTL() ? 'rtl' : 'ltr',
  pagination: false,
  arrows: false,
};

export function ViewGeneralSection({ isLoading, project }) {
  const sliderRef = useRef(null);

  const goPrev = () => {
    if (sliderRef.current && sliderRef.current.splide) {
      sliderRef.current.splide.go('<');
    }
  };

  const goNext = () => {
    if (sliderRef.current && sliderRef.current.splide) {
      sliderRef.current.splide.go('>');
    }
  };

  return (
    <Box width={1}>
      <Box width={1 / 2} mb={16}>
        <Typography color="textSecondary">
          {project ? project.description : ''}
        </Typography>
      </Box>
      <Box marginBottom={3}>
        <Row spacing={2}>
          <Col width={[4 / 12, 4 / 12]}>
            <InfoBlock
              icon={TimeCircle}
              label={<Trans>Created</Trans>}
              text={project ? formatFull(new Date(project.createdAt)) : 'N/A'}
            />
          </Col>
          <Col width={[4 / 12, 4 / 12]}>
            <InfoBlock
              icon={CalendarEdit}
              label={<Trans>Last update</Trans>}
              text={project ? formatFull(new Date(project.updatedAt)) : 'N/A'}
            />
          </Col>
          <Col width={[4 / 12, 4 / 12]}>
            <InfoBlock
              icon={PersonalCard}
              label={<Trans>Members</Trans>}
              text={project ? project.attendees.length + 2 : 'N/A'}
            />
          </Col>
        </Row>
        <Row spacing={2}>
          <Col width={[4 / 12, 4 / 12]}>
            <InfoBlock
              icon={Tasks}
              label={<Trans>Tasks</Trans>}
              text={propOrNa('tasksCount', project)}
            />
          </Col>
          <Col width={[4 / 12, 4 / 12]}>
            <InfoBlock
              icon={VideoCamera2}
              label={<Trans>Meetings</Trans>}
              text={propOrNa('meetingsCount', project)}
            />
          </Col>
          <Col width={[4 / 12, 4 / 12]}>
            <InfoBlock
              icon={Attach}
              label={<Trans>Attachments</Trans>}
              text={propOrNa('attachmentsCount', project)}
            />
          </Col>
        </Row>
        <Row spacing={2}>
          <Col width={[4 / 12, 4 / 12]}>
            <InfoBlock
              icon={CalendarEdit}
              label={<Trans>Start date</Trans>}
              text={
                project && project.startDate
                  ? format(new Date(project.startDate))
                  : 'N/A'
              }
            />
          </Col>
          <Col width={[4 / 12, 4 / 12]}>
            <InfoBlock
              icon={CalendarEdit}
              label={<Trans>End date</Trans>}
              text={
                project && project.endDate
                  ? format(new Date(project.endDate))
                  : 'N/A'
              }
            />
          </Col>
        </Row>
      </Box>
      <Card>
        <CardHeader marginBottom={3}>
          <CardTitle>Members</CardTitle>
          <Flex style={{ gap: 8 }}>
            <CircleButton color="text" onClick={goPrev}>
              <ArrowLeft color="#353C4D" size={18} />
            </CircleButton>
            <CircleButton onClick={goNext}>
              <ArrowRight color="#353C4D" size={18} />
            </CircleButton>
          </Flex>
        </CardHeader>
        <CardBody>
          <SkeletonRenderer
            height={280}
            loading={isLoading}
            render={() => (
              <Slider ref={sliderRef} id="members" options={sliderOptions}>
                {(project ? project.members : []).map((user, index) => (
                  <Slide key={user.id + user.role}>
                    <Link
                      to={`/hr/employees/${user.username}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <UserCard
                        style={{ height: '100%' }}
                        width={1}
                        avatar={user.avatar}
                        title={user.role}
                        name={getFullName(user, { middleKey: null })}
                        subtitle={
                          <span>
                            {user.department.name}
                            <br />
                            {user.jobTitle}
                          </span>
                        }
                      />
                    </Link>
                  </Slide>
                ))}
              </Slider>
            )}
          />
        </CardBody>
      </Card>
    </Box>
  );
}

ViewGeneralSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  project: PropTypes.shape({
    description: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
  }),
};

// eslint-disable-next-line react/prop-types
function InfoBlock({ label, text, icon, ...rest }) {
  const Icon = icon;
  return (
    <FlexStyle alignItems="center" justifyContent="space-between" {...rest}>
      <Flex alignItems="center" style={{ gap: 8 }}>
        <Icon />
        <Typography fontWeight={500} color="textSecondary" mr={2}>
          {label}:
        </Typography>
      </Flex>
      <Typography fontWeight={500}>{text}</Typography>
    </FlexStyle>
  );
}

export const FlexStyle = styled(Flex)`
  border: 1px solid #e9eaeb;
  border-radius: 16px;
  padding: 16px;
`;
